import { useState, useEffect, useCallback } from 'react';
import { Bell, Plus, Trash, CheckCircle, CircleNotch, EnvelopeSimple, SlackLogo } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import api from '../lib/api';
import { toast } from 'sonner';

export default function AlertRulesPanel({ linkId }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ threshold: '', email: true, slackUrl: '' });

  const fetchRules = useCallback(async () => {
    if (!linkId) return;
    setLoading(true);
    try {
      const res = await api.get(`/links/${linkId}/alerts`);
      setRules(res.data || []);
    } catch {
      console.error('Failed to load alert rules');
    } finally {
      setLoading(false);
    }
  }, [linkId]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const handleAdd = async () => {
    const t = parseInt(form.threshold);
    if (!t || t < 1) return toast.error('Enter a positive click count');
    if (!form.email && !form.slackUrl) return toast.error('Enable at least one notification channel');
    setSaving(true);
    try {
      await api.post(`/links/${linkId}/alerts`, {
        threshold: t,
        channels: { email: form.email, slack_url: form.slackUrl || null },
      });
      setForm({ threshold: '', email: true, slackUrl: '' });
      setShowForm(false);
      fetchRules();
      toast.success('Alert rule added');
    } catch (err) {
      toast.error(err.message || 'Failed to add alert');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Delete this alert rule?')) return;
    try {
      await api.delete(`/links/${linkId}/alerts/${ruleId}`);
      setRules(r => r.filter(x => x.rule_id !== ruleId));
      toast.success('Alert deleted');
    } catch {
      toast.error('Failed to delete alert');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-base font-bold text-foreground flex items-center gap-2">
            <Bell size={17} weight="duotone" className="text-primary" />
            Click threshold alerts
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">Get notified by email or Slack when this link hits a milestone.</p>
        </div>
        <Button size="sm" onClick={() => setShowForm(v => !v)} className="rounded-xl gap-1.5 h-8 px-3 font-semibold">
          <Plus size={13} weight="bold" /> Add alert
        </Button>
      </div>

      {/* Add form */}
      {showForm && (
        <div className="rounded-xl border border-primary/20 bg-primary/[0.03] p-4 space-y-4">
          <p className="text-xs font-semibold text-foreground">New alert rule</p>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-1.5 block">
              Alert me when clicks reach
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                value={form.threshold}
                onChange={e => setForm(f => ({ ...f, threshold: e.target.value }))}
                placeholder="e.g. 1000"
                className="w-36 rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
              />
              <span className="text-sm text-muted-foreground">clicks</span>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Notification channels</p>
            <div className="flex items-center justify-between rounded-lg border border-border/80 bg-background px-3 py-2.5">
              <div className="flex items-center gap-2">
                <EnvelopeSimple size={15} className="text-muted-foreground" />
                <span className="text-sm font-medium">Email</span>
              </div>
              <Switch checked={form.email} onCheckedChange={v => setForm(f => ({ ...f, email: v }))} />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 mb-1">
                <SlackLogo size={15} className="text-muted-foreground" />
                <span className="text-sm font-medium">Slack webhook</span>
                <span className="text-[10px] text-muted-foreground">(optional)</span>
              </div>
              <input
                type="url"
                value={form.slackUrl}
                onChange={e => setForm(f => ({ ...f, slackUrl: e.target.value }))}
                placeholder="https://hooks.slack.com/services/..."
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <Button size="sm" onClick={handleAdd} disabled={saving} className="rounded-xl gradient-primary text-white shadow-glow font-semibold gap-1.5">
              {saving ? <CircleNotch size={13} className="animate-spin" /> : <CheckCircle size={13} />}
              Save alert
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowForm(false)} className="rounded-xl border-border">Cancel</Button>
          </div>
        </div>
      )}

      {/* Rules list */}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
          <CircleNotch size={14} className="animate-spin" /> Loading…
        </div>
      ) : rules.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border/80 bg-muted/10 py-10 text-center">
          <Bell size={32} className="mx-auto mb-2 text-muted-foreground opacity-30" />
          <p className="text-sm font-semibold text-muted-foreground">No alerts yet</p>
          <p className="text-xs text-muted-foreground mt-1">Add your first milestone to get notified when your link goes viral.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rules.map(rule => (
            <div key={rule.rule_id} className="rounded-xl border border-border/80 bg-white px-4 py-3 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-foreground">
                    {rule.threshold.toLocaleString()} clicks
                  </span>
                  <div className="flex gap-1">
                    {rule.channels?.email && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-semibold">
                        <EnvelopeSimple size={9} /> Email
                      </span>
                    )}
                    {rule.channels?.slack_url && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 text-purple-700 px-2 py-0.5 text-[10px] font-semibold">
                        <SlackLogo size={9} /> Slack
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {rule.fired_at ? (
                    <span className="text-violet-600 font-medium">✓ Fired {new Date(rule.fired_at).toLocaleDateString()}</span>
                  ) : (
                    <span className="text-emerald-600 font-medium">● Watching</span>
                  )}
                </p>
              </div>
              <button
                onClick={() => handleDelete(rule.rule_id)}
                className="h-8 w-8 grid place-items-center rounded-lg text-muted-foreground hover:text-rose-500 hover:bg-rose-50 transition-colors shrink-0"
              >
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-dashed border-border/70 bg-muted/10 p-5 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Roadmap</p>
        <p className="text-xs text-muted-foreground leading-relaxed">
          These alert styles match the LinkFlow detail mockups. Threshold click alerts above are live; the monitors below require
          scheduled analytics evaluation and will ship in a future release.
        </p>
        <div className="space-y-2">
          {[
            ['Conversion drop vs baseline', 'Email + Slack when attached conversion KPI falls below your trailing average.'],
            ['Click anomaly spike', 'Fires when short-window velocity deviates sharply from seasonal baseline.'],
            ['Bot fraction threshold', 'Useful when social or paid sources send abnormal crawler mixes.'],
          ].map(([t, sub]) => (
            <div
              key={t}
              className="rounded-lg border border-border/60 bg-background/60 px-3 py-2.5 flex items-start gap-3"
            >
              <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-muted text-muted-foreground shrink-0">
                Soon
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground">{t}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
